import sortBy from 'lodash/sortBy'
import { useMemo, useState } from 'react'

import { SORT_DIRECTION_ASC, SORT_DIRECTION_DESC } from '../constants/sort'

export type ColumnDirectionType = typeof SORT_DIRECTION_ASC | typeof SORT_DIRECTION_DESC

interface IUseTableSortProps<T> {
  defaultSortedColumn: string
  defaultSortDirection?: ColumnDirectionType
  listToSort: Array<T>
}

interface IUseTableSortResults<T> {
  handleChangeSortDirection: (columnToSort: string) => void
  sortedColumn: string
  columnDirection: ColumnDirectionType
  sortedList: Array<T>
}

const flipDirection = (direction: ColumnDirectionType) =>
  direction === SORT_DIRECTION_ASC ? SORT_DIRECTION_DESC : SORT_DIRECTION_ASC

export function useTableSortBy<T>({
  defaultSortedColumn,
  defaultSortDirection = SORT_DIRECTION_ASC,
  listToSort,
}: IUseTableSortProps<T>): IUseTableSortResults<T> {
  const [columnDirection, setColumnDirection] = useState<ColumnDirectionType>(defaultSortDirection)
  const [sortedColumn, setSortedColumn] = useState(defaultSortedColumn)
  const [sortedList, setSortedList] = useState(listToSort)

  useMemo(() => {
    const updatedSortedList =
      columnDirection === SORT_DIRECTION_ASC
        ? sortBy(listToSort, sortedColumn)
        : sortBy(listToSort, sortedColumn).reverse()

    setSortedList(updatedSortedList)
  }, [listToSort, columnDirection, sortedColumn])

  function handleChangeSortDirection(columnToSort: string) {
    const isPreviousSortedColumn = columnToSort === sortedColumn
    const updatedColumnDirection = isPreviousSortedColumn
      ? flipDirection(columnDirection)
      : SORT_DIRECTION_ASC

    setSortedColumn(columnToSort)
    return setColumnDirection(updatedColumnDirection)
  }

  return { sortedColumn, columnDirection, sortedList, handleChangeSortDirection }
}
